import { supabase } from "./supabase.js";
import type {
  CommentNode,
  ContentBlock,
  ContentFormat,
  CurrentUser,
  FavouriteInput,
  FavouriteItem,
  Paginated,
  ReviewDetail,
  ReviewInput,
  ReviewQuery,
  ReviewSummary,
  SignedUpload,
  TagCount,
} from "./types.js";

/**
 * The Cloudflare Worker's origin in production, set by VITE_API_URL when the
 * site is built; Vite proxies `/api` to the Node server in dev.
 *
 * Trailing slashes are stripped because every `path` below already starts with
 * `/api`, so an origin ending in `/` produced `https://host//api/me`. The API
 * 404s that doubled path — including the CORS preflight, which is why it
 * surfaced as "preflight ... does not have HTTP ok status" rather than as a
 * plain 404.
 */
const BASE_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/+$/, "");

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(
    status: number,
    code: string,
    message: string,
    details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Field-level messages from a failed zod validation, if the server sent any. */
  get fieldErrors(): { field: string; message: string }[] {
    return Array.isArray(this.details)
      ? (this.details as { field: string; message: string }[])
      : [];
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Attach the caller's session token. Required for anything non-public. */
  authenticated?: boolean;
  signal?: AbortSignal;
}

async function request<T>(
  path: string,
  { method = "GET", body, authenticated = false, signal }: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};

  if (body !== undefined) headers["Content-Type"] = "application/json";

  if (authenticated) {
    // getSession() refreshes an expired token before handing it over, so a
    // long-idle tab does not fire a doomed request.
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;

    if (!token) {
      throw new ApiError(401, "unauthorized", "Please sign in and try again.");
    }

    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: signal ?? null,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;

    throw new ApiError(
      response.status,
      error?.code ?? "request_failed",
      error?.message ?? "Something went wrong. Please try again.",
      error?.details,
    );
  }

  return payload as T;
}

function toQueryString(query: ReviewQuery): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

export const api = {
  me: (signal?: AbortSignal) =>
    request<{ user: CurrentUser | null }>("/api/me", {
      authenticated: true,
      signal,
    }),

  listReviews: (query: ReviewQuery = {}, signal?: AbortSignal) =>
    request<Paginated<ReviewSummary>>(
      `/api/reviews${toQueryString(query)}`,
      // Drafts are only returned to an admin, and only when the token is sent.
      { authenticated: query.includeDrafts === true, signal },
    ),

  listTags: (signal?: AbortSignal) =>
    request<{ items: TagCount[] }>("/api/reviews/tags", { signal }),

  /**
   * The review pinned to the home page. `review` is null only when nothing has
   * been published at all — the API falls back to the newest write-up when no
   * feature has been chosen, so the page never has to ask twice.
   */
  getFeaturedReview: (signal?: AbortSignal) =>
    request<{ review: ReviewSummary | null }>("/api/reviews/featured", {
      signal,
    }),

  getReview: (slug: string, options: { authenticated?: boolean } = {}, signal?: AbortSignal) =>
    request<ReviewDetail>(`/api/reviews/${encodeURIComponent(slug)}`, {
      authenticated: options.authenticated ?? false,
      signal,
    }),

  createReview: (input: ReviewInput) =>
    request<ReviewDetail>("/api/reviews", {
      method: "POST",
      body: input,
      authenticated: true,
    }),

  updateReview: (id: string, input: Partial<ReviewInput>) =>
    request<ReviewDetail>(`/api/reviews/${id}`, {
      method: "PATCH",
      body: input,
      authenticated: true,
    }),

  deleteReview: (id: string) =>
    request<void>(`/api/reviews/${id}`, {
      method: "DELETE",
      authenticated: true,
    }),

  listComments: (slug: string, signal?: AbortSignal) =>
    request<{ items: CommentNode[] }>(
      `/api/reviews/${encodeURIComponent(slug)}/comments`,
      { signal },
    ),

  createComment: (slug: string, body: string, parentCommentId?: string | null) =>
    request<CommentNode>(`/api/reviews/${encodeURIComponent(slug)}/comments`, {
      method: "POST",
      body: { body, parentCommentId: parentCommentId ?? null },
      authenticated: true,
    }),

  deleteComment: (id: string) =>
    request<void>(`/api/comments/${id}`, {
      method: "DELETE",
      authenticated: true,
    }),

  // -- editable page copy ---------------------------------------------------

  listContent: (signal?: AbortSignal) =>
    request<{ blocks: Record<string, ContentBlock> }>("/api/content", { signal }),

  /**
   * Saves one block. An empty `value` deletes the override, which is how the
   * UI resets a block to the copy hard-coded in the page — so `block` comes
   * back null in that case rather than as an empty string.
   */
  updateContent: (key: string, value: string, format: ContentFormat = "text") =>
    request<{ block: ContentBlock | null }>(
      `/api/content/${encodeURIComponent(key)}`,
      { method: "PATCH", body: { value, format }, authenticated: true },
    ),

  // -- favourites -----------------------------------------------------------

  listFavourites: (signal?: AbortSignal) =>
    request<{ items: FavouriteItem[] }>("/api/favourites", { signal }),

  /** Whole list at once: array order becomes the on-page order. */
  replaceFavourites: (items: FavouriteInput[]) =>
    request<{ items: FavouriteItem[] }>("/api/favourites", {
      method: "PUT",
      body: { items },
      authenticated: true,
    }),

  // -- uploads --------------------------------------------------------------

  /**
   * Two-step image upload: ask the API for a signed URL (admin only), then PUT
   * the file straight to Supabase Storage. The bytes never pass through the
   * API.
   *
   * Returns both forms: the cover image stores the `path` (the API resolves it
   * on read), while an image embedded in the body needs the absolute `url`.
   */
  uploadReviewImage: (file: File) => uploadTo("/api/uploads/review-image", file),

  /** The same, for images that belong to the site rather than to one review. */
  uploadSiteImage: (file: File) => uploadTo("/api/uploads/site-image", file),
};

async function uploadTo(
  endpoint: string,
  file: File,
): Promise<{ path: string; url: string | null }> {
  const upload = await request<SignedUpload>(endpoint, {
    method: "POST",
    body: { contentType: file.type },
    authenticated: true,
  });

  const response = await fetch(upload.signedUrl, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });

  if (!response.ok) {
    throw new ApiError(
      response.status,
      "upload_failed",
      "The image could not be uploaded. Please try again.",
    );
  }

  return { path: upload.path, url: upload.publicUrl };
}
